/**
 * Exact decimal arithmetic on strings.
 *
 * Amounts and units arrive from LIV as decimal strings ("50000.00",
 * "1500.5000") and the store keeps them as DynamoDB numbers. A JavaScript float
 * cannot hold 0.1 exactly, so a sum of three such floats is already wrong in
 * the last place - and a TM total that is wrong in the last place is a
 * reconciliation break. Every value here is a BigInt scaled by a power of ten;
 * no Number arithmetic touches an amount (spec 23.2.2 bans floats).
 *
 * JSON numbers are accepted as INPUT (holdingAtRequest and the raw figures are
 * numbers on the wire). They are read through String(n), which is the shortest
 * text that round-trips to the same double - "753170.3028000001" stays exactly
 * that - and then rounded here, in decimal, to the column's scale.
 *
 * Rounding is half away from zero: 0.005 -> 0.01, -0.005 -> -0.01.
 */

const DECIMAL = /^([+-]?)(\d+)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/;

/** A decimal as { n: BigInt unscaled value, s: number of fraction digits }. */
function read(value) {
  if (typeof value === 'bigint') return { n: value, s: 0 };
  let text = value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new TypeError('not a finite number');
    text = String(value);
  }
  if (typeof text !== 'string') throw new TypeError('a decimal must be a string or a number');
  const match = DECIMAL.exec(text.trim());
  if (!match) throw new TypeError('not a decimal number');
  const [, sign, whole, fraction = '', exponent = '0'] = match;
  let scale = fraction.length - Number(exponent);
  let n = BigInt(whole + fraction);
  if (scale < 0) {
    n *= 10n ** BigInt(-scale);
    scale = 0;
  }
  return { n: sign === '-' ? -n : n, s: scale };
}

function pow10(k) {
  return 10n ** BigInt(k);
}

/** Rescale to exactly `to` fraction digits, rounding half away from zero. */
function rescale({ n, s }, to) {
  if (to >= s) return n * pow10(to - s);
  const divisor = pow10(s - to);
  const q = n / divisor;
  const r = n % divisor;
  const twice = (r < 0n ? -r : r) * 2n;
  if (twice >= divisor) return n < 0n ? q - 1n : q + 1n;
  return q;
}

function write(n, s) {
  const negative = n < 0n;
  let digits = (negative ? -n : n).toString();
  if (s > 0) {
    digits = digits.padStart(s + 1, '0');
    digits = digits.slice(0, -s) + '.' + digits.slice(-s);
  }
  return (negative ? '-' : '') + digits;
}

function align(a, b) {
  const s = Math.max(a.s, b.s);
  return [a.n * pow10(s - a.s), b.n * pow10(s - b.s), s];
}

/** True for a string or number this module can read. */
export function isDecimal(value) {
  if (value === null || value === undefined) return false;
  try {
    read(value);
    return true;
  } catch {
    return false;
  }
}

/** The value with exactly `dp` fraction digits. */
export function toFixed(value, dp) {
  return write(rescale(read(value), dp), dp);
}

/** a + b, at the larger of the two scales. */
export function add(a, b) {
  const [x, y, s] = align(read(a), read(b));
  return write(x + y, s);
}

/** a - b. */
export function sub(a, b) {
  const [x, y, s] = align(read(a), read(b));
  return write(x - y, s);
}

/** The sum of a list; '0' for an empty one. */
export function sum(values) {
  return values.reduce((total, value) => add(total, value), '0');
}

/** a * b, exactly (the scales add). */
export function mul(a, b) {
  const x = read(a);
  const y = read(b);
  return write(x.n * y.n, x.s + y.s);
}

/** a / b to `dp` fraction digits, rounded half away from zero. */
export function div(a, b, dp) {
  const x = read(a);
  const y = read(b);
  if (y.n === 0n) throw new RangeError('division by zero');
  // a/b = (xn / 10^xs) / (yn / 10^ys); scaled by 10^dp that is
  // xn * 10^(ys + dp) / (yn * 10^xs).
  let num = x.n * pow10(y.s + dp);
  let den = y.n * pow10(x.s);
  if (den < 0n) {
    num = -num;
    den = -den;
  }
  const q = num / den;
  const r = num % den;
  const twice = (r < 0n ? -r : r) * 2n;
  const rounded = twice >= den ? (num < 0n ? q - 1n : q + 1n) : q;
  return write(rounded, dp);
}

/** part / whole * 100, to `dp` fraction digits. */
export function percent(part, whole, dp = 4) {
  return div(mul(part, '100'), whole, dp);
}

/** -1, 0 or 1. */
export function cmp(a, b) {
  const [x, y] = align(read(a), read(b));
  if (x === y) return 0;
  return x < y ? -1 : 1;
}

/** The largest of a list, exactly as given; null for an empty one. */
export function max(values) {
  let best = null;
  for (const value of values) {
    if (best === null || cmp(value, best) > 0) best = value;
  }
  return best;
}

/** True when the value is zero. */
export function isZero(value) {
  return read(value).n === 0n;
}

/**
 * The canonical text of a number: no exponent, no leading zeros, no trailing
 * fraction zeros ("0050.500" -> "50.5", "-0" -> "0"). DynamoDB stores numbers
 * this way, so it is what a read returns.
 */
export function canonical(value) {
  const { n, s } = read(value);
  let unscaled = n;
  let scale = s;
  while (scale > 0 && unscaled % 10n === 0n) {
    unscaled /= 10n;
    scale -= 1;
  }
  return write(unscaled, scale);
}
